import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DASHBOARD_URL } from '../config/urls';
import './AboutUs.css';

// Icons
const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
  </svg>
);

/* Animations */
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } }
};

const AboutUs = () => {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '-50px 0px' }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div className="about-root" ref={ref}>
      {/* Background Gradient */}
      <div className="bg"></div>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-wrap">
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="hero-content"
          >
            <motion.span className="eyebrow" variants={fadeInUp}>
              About LendingMitra
            </motion.span>

            <motion.h1 variants={fadeInUp}>
              Empowering Your Financial <span className="highlight">Journey</span>
            </motion.h1>

            <motion.p variants={fadeInUp}>
              At LendingMitra, we believe that access to credit should be simple, 
              transparent, and fast. Our mission is to empower individuals and 
              businesses with the financial tools they need to grow and thrive.
            </motion.p>

            <motion.div variants={fadeInUp} className="cta-buttons">
              <Link to="/loans" className="btn btn-primary">
                Explore Loan Options
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="about-values">
        <div className="about-wrap">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>Our Core Values</motion.h2>
            
            <motion.p className="section-subtitle" variants={fadeInUp}>
              These principles guide everything we do at LendingMitra
            </motion.p>

            <motion.div className="values-grid" variants={staggerContainer}>
              {[
                {
                  title: "Customer First",
                  description: "We put our customers at the heart of everything we do, ensuring their needs are always our top priority.",
                  icon: <CheckIcon />
                },
                {
                  title: "Transparency",
                  description: "Clear terms, no hidden fees, and honest communication at every step of the process.",
                  icon: <CheckIcon />
                },
                {
                  title: "Innovation",
                  description: "Leveraging cutting-edge technology to create seamless and efficient financial solutions.",
                  icon: <CheckIcon />
                },
                {
                  title: "Integrity",
                  description: "We conduct our business with the highest ethical standards and accountability.",
                  icon: <CheckIcon />
                },
                {
                  title: "Accessibility",
                  description: "Making financial services available to everyone, regardless of their background or circumstances.",
                  icon: <CheckIcon />
                },
                {
                  title: "Excellence",
                  description: "Committed to delivering the highest quality service and support to our customers.",
                  icon: <CheckIcon />
                }
              ].map((value, index) => (
                <motion.div 
                  key={index} 
                  className="value-card" 
                  variants={fadeInUp}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <div className="value-icon">
                    {value.icon}
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="about-story">
        <div className="story-wrap">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>Our Story</motion.h2>
            <motion.p variants={fadeInUp}>
              Founded with a vision to transform the lending landscape, LendingMitra 
              was born out of a simple idea: financial services should be accessible, 
              transparent, and designed with the customer in mind.
            </motion.p>

            <motion.div className="principles" variants={staggerContainer}>
              {[
                {
                  title: "Our Mission",
                  description: "To simplify access to credit through innovative, technology-driven solutions that empower individuals and businesses.",
                  icon: "🎯"
                },
                {
                  title: "Our Vision",
                  description: "To be the most trusted financial partner for millions, enabling financial freedom and growth opportunities.",
                  icon: "🔭"
                },
                {
                  title: "Our Promise",
                  description: "We're committed to providing fair, transparent, and responsible lending solutions that meet your unique needs.",
                  icon: "🤝"
                }
              ].map((item, index) => (
                <motion.div 
                  key={index} 
                  className="principle"
                  variants={fadeInUp}
                >
                  <div className="principle-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="leadership">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="leadership-wrap"
        >
          <motion.h2 variants={fadeInUp}>Meet Our Leadership</motion.h2>
          <motion.p className="section-subtitle" variants={fadeInUp}>
            Experienced professionals dedicated to your financial success
          </motion.p>

          <div className="leaders">
            {[
              {
                name: " Mrs. Jyoti Agrawal",
                role: "Managing Director",
                bio: "Jyoti brings extensive experience and strategic insight to the organization. With a strong background in leadership and operations, they play a key role in shaping the company’s vision and long-term growth.",
                initials: "JA"
              },
              {
                name: "Ms. Kanishka Gandhi",
                role: "Director",
                bio: "Kanishka contributes valuable expertise in management and decision-making, ensuring the company maintains high standards of performance and professionalism.",
                initials: "KG"
              }
            ].map((leader, index) => (
              <motion.div 
                key={index} 
                className="leader"
                variants={fadeInUp}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="leader-head">
                  <div className="leader-avatar">{leader.initials}</div>
                  <div>
                    <h3 className="leader-name">{leader.name}</h3>
                    <div className="leader-role">{leader.role}</div>
                  </div>
                </div>
                <p>{leader.bio}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Call to Action */}
      <section className="about-cta">
        <div className="about-wrap">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="cta-content"
          >
            <motion.h2 variants={fadeInUp}>Ready to Get Started?</motion.h2>
            <motion.p variants={fadeInUp}>
              Join thousands of satisfied customers who trust LendingMitra for their financial needs.
              Apply now and experience the difference.
            </motion.p>
            <motion.div variants={fadeInUp} className="cta-buttons">
              <a href={DASHBOARD_URL} className="btn btn-primary">
                Apply for a Loan
              </a>
              <Link to="/contact" className="btn btn-outline">
                Contact Our Team
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final Note */}
      <section className="about-close">
        <div className="about-wrap">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            At LendingMitra, we're more than just a lending platform—we're your 
            financial partner, committed to helping you achieve your dreams and 
            build a better future.
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
